import { onMounted, ref, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import parseMobile from 'libphonenumber-js/mobile';
import { number, object, string } from 'yup';

import router from '@/router';
import { useWorldStore } from '@/stores/world.store';
import { useField, useForm } from 'vee-validate';
import type { City } from '../../interfaces/world/city.interface';
import type { Country } from '../../interfaces/world/country.interface';

export const useSupplierForm = () => {
  const { t } = useI18n();
  const { getCountriesList } = useWorldStore();

  const validationSchema = object({
    supplierNumber: number()
      .typeError(t('suppliers.titles.supplier_number_validation'))
      .required()
      .label(t('suppliers.titles.supplier_number')),
    supplierName: string().required().max(255).label(t('suppliers.titles.supplier_name')),
    address: string().nullable(),
    zipCode: number().typeError(t('suppliers.errors.zip_code_number')).nullable(),
    country: string().max(20).label(t('suppliers.titles.country')).nullable(),
    city: string().max(30).label(t('suppliers.titles.city')).nullable(),
    phone: string().test('validate-phone-number', t('suppliers.titles.invalid_phone'), value => {
      if (!value) return true;

      return (
        ((parseMobile(value as string) && parseMobile(value as string)?.isValid()) as boolean) &&
        value.length <= 20
      );
    }),
    supplierEmail: string().email().max(254).label(t('suppliers.titles.supplier_email')).nullable(),
    customer: string()
      .typeError(t('suppliers.titles.customer_required'))
      .required()
      .label(t('suppliers.titles.select_customer'))
      .default(''),
  });

  const { errors, handleSubmit, isSubmitting, meta, resetForm, setValues, setFieldValue } = useForm(
    {
      validationSchema,
    }
  );
  const { value: supplierNumber } = useField('supplierNumber');
  const { value: supplierName } = useField('supplierName');
  const { value: address } = useField('address');
  const { value: zipCode } = useField('zipCode');
  const { value: country } = useField('country', undefined, {
    initialValue: null,
  });
  const {
    value: city,
    resetField: resetCity,
    setValue: setCityValue,
  } = useField('city', undefined, {
    initialValue: null,
  });
  const { value: phone } = useField('phone');
  const { value: supplierEmail } = useField('supplierEmail');
  const { value: customer } = useField('customer', undefined, {
    initialValue: null,
  });

  const countriesList: Ref<Country[]> = ref([]);
  const citiesList: Ref<City[]> = ref([]);
  const countriesLoading = ref(false);

  onMounted(async () => {
    countriesLoading.value = true;
    try {
      const countries = await getCountriesList();

      countriesList.value = countries.map(country => {
        country[
          'displayed_name'
        ] = `${country.flag_emoji} ${country.name} | ${country.native_name}`;

        return country;
      });
    } finally {
      countriesLoading.value = false;
    }
  });

  const backToSuppliersList = () => {
    router.push({ name: 'list-suppliers' });
  };

  return {
    errors,
    handleSubmit,
    isSubmitting,
    meta,
    resetForm,
    setValues,
    supplierNumber,
    supplierName,
    address,
    zipCode,
    country,
    city,
    setCityValue,
    resetCity,
    phone,
    supplierEmail,
    countriesList,
    citiesList,
    countriesLoading,
    backToSuppliersList,
    customer,
    setFieldValue,
  };
};
